import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { UserPlus, Loader2, Users, Trash2, Copy, Check, KeyRound, ShieldAlert } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const roleStyles = {
  owner: 'bg-blue-100 text-blue-700 border-blue-200',
  admin: 'bg-purple-100 text-purple-700 border-purple-200',
  editor: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  viewer: 'bg-slate-100 text-slate-700 border-slate-200',
};

const ROLE_LABEL = {
  owner: 'Primary',
  admin: 'Admin',
  editor: 'Editor',
  viewer: 'Viewer',
};

const statusStyles = {
  active: 'bg-emerald-50 text-emerald-700',
  pending: 'bg-amber-50 text-amber-700',
};

export default function AdminClientTeamCard({ clientId, clientEmail, businessName }) {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [lastLink, setLastLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const fetchTeam = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/clients/${clientId}/team`);
      setTeam(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load team');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) fetchTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Email is required');
      return;
    }
    setInviting(true);
    try {
      const r = await axios.post(
        `${API}/admin/clients/${clientId}/team/invite`,
        { email: email.trim().toLowerCase(), role },
      );
      toast.success(r.data.message || 'Invitation created');
      setEmail('');
      setRole('viewer');
      setLastLink({
        url: window.location.origin + r.data.magic_link,
        email: r.data.member.email,
      });
      setLinkCopied(false);
      await fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite team member');
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (member) => {
    try {
      await axios.delete(`${API}/admin/clients/${clientId}/team/${member.id}`);
      toast.success(`${member.email} removed`);
      await fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove team member');
    }
  };

  const handleRoleChange = async (member, newRole) => {
    if (member.role === newRole) return;
    try {
      await axios.put(
        `${API}/admin/clients/${clientId}/team/${member.id}/role`,
        null,
        { params: { role: newRole } },
      );
      toast.success(`Role updated to ${newRole}`);
      await fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleWipeAccount = async (member) => {
    const isPrimary = member.role === 'owner';
    const confirmMsg = isPrimary
      ? `⚠ DANGER · Wipe the Primary account ${member.email}?\n\n` +
        `This will permanently DELETE:\n` +
        `  • the user account ${member.email}\n` +
        `  • the entire workspace${businessName ? ' "' + businessName + '"' : ''}\n` +
        `  • ALL invoices, bills, customers, suppliers, bank balances and Xero sync data\n\n` +
        `Type OK in the next prompt to proceed.`
      : `This will fully DELETE the user account for ${member.email} and generate a fresh magic link.\n\n` +
        `Use this only when their password reset isn't working.\n\n` +
        `Proceed?`;
    if (!window.confirm(confirmMsg)) return;
    if (isPrimary) {
      const typed = window.prompt('Type OK (uppercase) to confirm full client wipe:');
      if (typed !== 'OK') {
        toast.info('Cancelled');
        return;
      }
    }
    try {
      const r = await axios.delete(`${API}/admin/clients/${clientId}/team/${member.id}/account`);
      if (r.data.deleted_client_workspace) {
        toast.success(r.data.message, { duration: 6000 });
        // Workspace is gone — bounce admin back to the clients list
        setTimeout(() => { window.location.href = '/admin'; }, 1500);
        return;
      }
      const url = window.location.origin + r.data.new_magic_link;
      setLastLink({ url, email: member.email });
      setLinkCopied(false);
      toast.success(r.data.message + ' — fresh magic link ready above.', { duration: 6000 });
      await fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to wipe account');
    }
  };

  const copyLink = () => {
    if (!lastLink) return;
    navigator.clipboard.writeText(lastLink.url);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <Card data-testid="admin-client-team-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="w-4 h-4" />
          Team Members
          <span className="text-xs font-normal text-slate-500">
            · {businessName || clientEmail}
          </span>
        </CardTitle>
        <p className="text-[11px] text-slate-500 mt-1">
          Roles below apply <strong>only to this client's workspace</strong>.
          Members cannot see any other client's data.
          <span className="ml-1 text-slate-400">"Primary" = the main account holder for this business.</span>
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Invite form */}
        <form
          onSubmit={handleInvite}
          className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end p-3 bg-slate-50 dark:bg-slate-900/40 rounded-lg border border-slate-200 dark:border-slate-700"
          data-testid="admin-invite-team-form"
        >
          <div className="flex-1 space-y-1">
            <Label htmlFor="admin-team-email" className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
              Invite by email
            </Label>
            <Input
              id="admin-team-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teammate@company.com.au"
              className="h-9"
              data-testid="admin-team-invite-email"
            />
          </div>
          <div className="space-y-1 w-full sm:w-40">
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
              Role
            </Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="h-9" data-testid="admin-team-invite-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="viewer">Viewer · read-only</SelectItem>
                <SelectItem value="editor">Editor · edit data</SelectItem>
                <SelectItem value="admin">Admin · full access</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            type="submit"
            disabled={inviting || !email.trim()}
            className="gap-1.5 h-9"
            data-testid="admin-team-invite-submit"
          >
            {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Send invite
          </Button>
        </form>

        {/* Last generated magic link */}
        {lastLink && (
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-900/40 text-xs"
            data-testid="admin-team-magic-link"
          >
            <span className="font-medium text-blue-900 dark:text-blue-200 shrink-0">
              Magic link for {lastLink.email}:
            </span>
            <code className="flex-1 truncate text-blue-700 dark:text-blue-300 font-mono">
              {lastLink.url}
            </code>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={copyLink}
              className="h-7 gap-1 shrink-0"
              data-testid="admin-team-copy-link"
            >
              {linkCopied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {linkCopied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        )}

        {/* Team list */}
        {loading ? (
          <div className="flex items-center justify-center py-6 text-slate-500 text-sm gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Email</TableHead>
                  <TableHead className="text-xs">Role</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Joined</TableHead>
                  <TableHead className="text-xs text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {team.map((m) => (
                  <TableRow key={m.id} data-testid={`team-row-${m.id}`}>
                    <TableCell className="text-sm font-medium">{m.email}</TableCell>
                    <TableCell>
                      {m.role === 'owner' ? (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${roleStyles.owner}`}
                          title="Primary account holder for this business. Has full control of THIS business only — does not affect other clients."
                        >
                          {ROLE_LABEL.owner}
                        </span>
                      ) : (
                        <Select
                          value={m.role}
                          onValueChange={(v) => handleRoleChange(m, v)}
                        >
                          <SelectTrigger
                            className={`h-7 w-28 text-[11px] font-semibold border ${roleStyles[m.role] || 'bg-slate-100 text-slate-700'}`}
                            data-testid={`team-role-${m.id}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="viewer">Viewer</SelectItem>
                            <SelectItem value="editor">Editor</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${statusStyles[m.status] || 'bg-slate-100 text-slate-700'}`}>
                        {m.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {m.joined_at ? new Date(m.joined_at).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <ResetPasswordButton
                          clientId={clientId}
                          member={m}
                          onDone={fetchTeam}
                        />
                        {/* Primary/owner can be wiped too — EYB admin always has authority */}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 h-7 px-2"
                          onClick={() => handleWipeAccount(m)}
                          title={m.role === 'owner'
                            ? "Wipe Primary account & generate fresh magic link (you'll need to re-invite them)"
                            : "Wipe user account & generate fresh magic link"}
                          data-testid={`team-wipe-${m.id}`}
                        >
                          <ShieldAlert className="w-3.5 h-3.5" />
                        </Button>
                        {m.role !== 'owner' && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                                data-testid={`team-remove-${m.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove team member?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  {m.email} will lose access to {businessName || 'this business'} immediately. They can be re-invited later.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleRemove(m)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Remove
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const generateTempPassword = () => {
  // 12 chars with letter + number + symbol
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnpqrstuvwxyz';
  const digits = '23456789';
  const symbols = '!@#$%&*';
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  let pw = pick(upper) + pick(lower) + pick(digits) + pick(symbols);
  const all = upper + lower + digits + symbols;
  while (pw.length < 12) pw += pick(all);
  return pw.split('').sort(() => Math.random() - 0.5).join('');
};

function ResetPasswordButton({ clientId, member, onDone }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleOpen = (v) => {
    setOpen(v);
    if (v) {
      setPw(generateTempPassword());
      setCopied(false);
    }
  };

  const submit = async () => {
    if (!pw || pw.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/admin/clients/${clientId}/team/${member.id}/reset-password`,
        { new_password: pw },
      );
      toast.success(r.data.message || 'Password reset');
      onDone && onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(pw);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-7 px-2"
          data-testid={`team-reset-pw-${member.id}`}
          title="Set / reset password for this team member"
        >
          <KeyRound className="w-3.5 h-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="team-reset-pw-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reset password for {member.email}</DialogTitle>
          <DialogDescription>
            Set a temporary password and share it with the user. They'll be asked to change it on next login. Use this when the user can't log in but their invite hasn't gone through cleanly.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Label className="text-xs">Temporary password</Label>
          <div className="flex gap-2">
            <Input
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="font-mono"
              data-testid="team-reset-pw-input"
            />
            <Button type="button" variant="outline" size="sm" onClick={copy} className="gap-1 shrink-0">
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setPw(generateTempPassword())}
              className="shrink-0"
              title="Generate a new random password"
            >
              ↻
            </Button>
          </div>
          <p className="text-[11px] text-slate-500">
            Min 8 chars · 1 letter · 1 number · 1 symbol
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="gap-1.5" data-testid="team-reset-pw-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Reset password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
